import { AfterViewInit, ChangeDetectorRef, Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { CellTypes, Column, Control, ControlTypes, Search, UtilService } from '@material-library';
import { ApiService } from 'src/app/core/api.service';
import { ActivityLogItemService } from './activity-log-item.service';
import { SyncCryptService } from 'src/app/core/crypt/sync-crypt.service';
import { Base64Service } from 'src/app/core/base64.service';
import { LoggerService } from 'src/app/core/logger.service';
import { ActivityIconMapping, EventOptions, EventResult, EventItem, EVENT_LIMIT, ActivityFilter, EventShareResponse, EventShare } from './activity-log.model';
import { EventReaderService } from 'src/app/shared/services/eventreader.service';
import { UserService } from 'src/app/core/user.service';

@Component({
  selector: 'app-activity-logs',
  templateUrl: './activity-logs.component.html',
  styleUrls: ['./activity-logs.component.scss']
})
export default class ActivityLogsComponent implements OnInit, AfterViewInit {
  @ViewChild('eventItemRef') eventItemRef: TemplateRef<any>;

  public activityLogs = [];
  public shares: EventShare[] = [];
  public isLoading: boolean = false;
  public isLoadMoreLoading: boolean = false;
  public offset: number = 0;
  public hasMore: boolean;
  public columns: Column[] = [];
  public shareId: string;
  public shareName: string;
  public shareSyncIds: { [id: number]: EventShare } = {};

  public searchConfig: Search = {
    placeholder: 'placeholder',
    label: 'label',
    size: 'default'
  };

  public activityFilter: ActivityFilter;

  public filterControls: Array<Control>;
  constructor(
    private apiService: ApiService,
    private activityLogItemService: ActivityLogItemService,
    private syncCryptService: SyncCryptService,
    private base64Service: Base64Service,
    private loggerService: LoggerService,
    private changeDetectorRef: ChangeDetectorRef,
    private utilService: UtilService,
    private userService: UserService,
    private eventReaderService: EventReaderService
  ) { }

  async ngOnInit(): Promise<void> {
    this.isLoading = true;
    this.resetFilters();
    await this.loadShares();
    await this.loadEvents();
    this.isLoading = false;
    this.initFilters();
  }

  ngAfterViewInit(): void {
    this.columns = [
      {
        key: 'type',
        header: 'Type',
        type: CellTypes.icon,
        width: '100px',
        cell: (event: Record<string, any>) => {
          return ActivityIconMapping[event.eventtype] || 'folder';
        },
      },
      {
        key: 'activityText',
        header: 'Event',
        isPrimary: true,
        type: CellTypes.templateRef,
        templateRef: this.eventItemRef,
        cell:(event: Record<string, any>) => {
          return {
            value: '',
            subText: event._id,
            overlineText: this.utilService.formatDate(event.servtime, 'MMM d, y h:mm a')
          }
        }
      },
      {
        key: 'servtime',
        header: 'Date',
        type: CellTypes.date,
        dateFormat:'MMM d, y h:mm a',
        isSortable: true,
        sortDirection: 'asc' as const,
        width: '200px',
        sort: (isSortAsc: boolean) => {
          this.offset = 0;
          this.activityLogs = [];
          this.loadEvents({
            offset: this.offset,
            sort: {servtime: isSortAsc ? -1 : 1}
          });
        }
      },
    ];

    this.changeDetectorRef.detectChanges();
  }

  async loadEvents(options?: EventOptions) {
    let apiResult: EventResult;
    options = {
      sort: {
        servtime: -1
      },
      ...options,
      limit: EVENT_LIMIT,
      offset: this.offset,
      ...((this.activityFilter.folder != 'all' && this.activityFilter.folder != 'all-shared-folder') && { share_ids: [this.activityFilter.folder] }),
      ...(this.activityFilter.folder === 'all-shared-folder' && { share_ids: this.shares.map(shareItem => shareItem.share_id) }),
      ...(this.activityFilter.activity != 'all' && { ActivityType: this.activityFilter.activity }),
      ...((this.activityFilter.daterange && this.activityFilter.daterange.date && this.activityFilter.daterange.endDate) ? { from: this.activityFilter.daterange.date, to: this.activityFilter.daterange.endDate } : {}),
    }
    
    const data: any = await this.eventReaderService.getActivityList({
      user_id: this.userService.get("id"),
      ...options
    });
    apiResult = data;
    const eventList = await this.processEventList(data.events);
    apiResult.events = eventList;
    this.activityLogs = [...this.activityLogs, ...apiResult.events];
    if (apiResult.events.length === EVENT_LIMIT) {
      this.hasMore = true;
    } else {
      this.hasMore = false;
    }
  }

  async loadShares() {
    try {
      const data: EventShareResponse = await this.apiService.execute('sharelistforuser', {}) as any;
      this.shares = await this.processShareList(data.shares);
      this.buildShareSyncIds();
    } catch (e) {
      this.loggerService.error('Error fetching share list');
    }
  }

  async onLoadMore() {
    this.isLoadMoreLoading = true;
    this.offset = this.offset + EVENT_LIMIT;
    await this.loadEvents();
    this.isLoadMoreLoading = false;
  }

  private async processEventList(events: EventItem[]) {
    const eventItems: Promise<EventItem>[] = [];
    for (let i = 0, len = events.length; i < len; i++) {
      eventItems.push(this.activityLogItemService.format(events[i]));
    }
    return await Promise.all(eventItems);
  }

  private async processShareList(list: EventShare[]) {
    let shareList: Promise<EventShare>[] = [];
    for (let i = 0, len = list.length; i < len; i++) {
      shareList.push(this._shareItem(list[i]));
    }
    return await Promise.all(shareList);
  }

  private async _shareItem(shareitem: EventShare) {
    try {
      const sname = await this.syncCryptService.filenameDecrypt(
        shareitem.enc_meta_name
      );
      shareitem.name = sname;
      shareitem.label = this.base64Service.decode(shareitem.label);
      shareitem.display_name = shareitem.label && shareitem.label !== sname
        ? `${sname} [ ${shareitem.label} ]`
        : sname;
      return shareitem;
    } catch (error) {
      this.loggerService.error(
        'Error decrypting share name for ' + shareitem.share_id
      );
      shareitem.display_name =
        this.base64Service.decode(shareitem.label) ||
        shareitem.share_id.toString();
      return shareitem;
    }
  }
  private initFilters() {
    const allSharedFolders = this.shares.map(share => {
      return {
        value: share.share_id,
        name: share.display_name
      }
    });
    allSharedFolders.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));

    this.filterControls = [{
      key: 'activity',
      label: 'All events',
      type: ControlTypes.select,
      selectConfig: {
        list: [
          {
            value: 'all',
            name: 'All events'
          },
          {
            value: 1,
            name: 'File & folder events'
          }, 
          {
            value: 2,
            name: 'Link events'
          }, 
          {
            value: 4,
            name: 'Shared folder user events'
          }, 
          {
            value: 3,
            name: 'Sign-in & security events'
          }
      ],
        label: 'All events'
      }
    }, {
      key: 'folder',
      label: 'All Folders',
      type: ControlTypes.select,
      selectConfig: {
        list: [{
          value: 'all',
          name: 'All folders'
        },{
          value: 'all-shared-folder',
          name: 'All shared folders',
          divider: true,
        }, ...allSharedFolders],
        label: 'All folders'
      }
    }, {
      key: 'daterange',
      label: 'Date range',
      type: ControlTypes.datePicker,
      datepickerConfig: {
        label: 'Date range',
        type: 'daterangepicker',
        maxDate: new Date()
      }
    }];
  }

  private buildShareSyncIds() {
    const shares: EventShare[] = this.shares;
    for (let i = 0, len = shares.length; i < len; i++) {
      this.shareSyncIds[shares[i].share_id] = shares[i];
      if (this.shareId == shares[i].share_id.toString()) {
        this.shareName = shares[i].name;
      }
    }
  }

  resetFilters() {
    this.activityFilter = {
      activity: 'all',
      daterange: {
        date: null,
        endDate: null
      },
      folder: 'all'
    };
  }

  onListFilterChange = (selectedFilters: any) => {
    if (!selectedFilters || !Object.keys(selectedFilters).length) {
      this.resetFilters()
      this.activityLogs = [];
      this.loadEvents();
      return;
    }
    if (selectedFilters?.daterange?.date && !selectedFilters?.daterange?.endDate) {
      return;
    }
    if(selectedFilters?.daterange?.date && selectedFilters?.daterange?.endDate) {
      selectedFilters.daterange.date = selectedFilters.daterange.date.setHours(0, 0, 0);
      selectedFilters.daterange.endDate = selectedFilters.daterange.endDate.setHours(23,59,59);
    }
    this.activityFilter = {
      ...this.activityFilter,
      ...selectedFilters
    };
    this.activityLogs = [];
    this.loadEvents();
  }
}
